import React from 'react';
import { Tag } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EditOutlined,
  EyeOutlined,
  FileTextOutlined,
  QuestionCircleOutlined,
  StarOutlined,
} from '@ant-design/icons';

export function unwrapList<T>(res: unknown): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res && typeof res === 'object') {
    const data = (res as { data?: unknown }).data;
    if (Array.isArray(data)) return data as T[];
    if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
      return (data as { data: T[] }).data;
    }
  }
  return [];
}

export function num(v: unknown, fallback = 0): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/** Map DB fields onto the UI assessment shape. */
export function normalizeAssessment<T extends Record<string, any>>(row: T): T & {
  score_percentage: number;
  assessment_result: string | null;
  total_score: number;
  max_possible_score: number;
} {
  return {
    ...row,
    score_percentage: num(row.score_percentage ?? row.percentage_score, 0),
    assessment_result: row.assessment_result ?? row.pass_fail_result ?? null,
    total_score: num(row.total_score, 0),
    max_possible_score: num(row.max_possible_score, 100),
  };
}

export function getAssessmentStatusTag(status: string) {
  const statusConfig: Record<string, { color: string; icon: React.ReactNode }> = {
    Draft: { color: 'default', icon: <EditOutlined /> },
    Submitted: { color: 'blue', icon: <FileTextOutlined /> },
    'Under-Review': { color: 'processing', icon: <EyeOutlined /> },
    Evaluated: { color: 'orange', icon: <StarOutlined /> },
    Approved: { color: 'green', icon: <CheckCircleOutlined /> },
    Rejected: { color: 'red', icon: <CloseCircleOutlined /> },
  };
  const config = statusConfig[status] || { color: 'default', icon: null };
  return (
    <Tag color={config.color} icon={config.icon}>
      {status}
    </Tag>
  );
}

export function getAssessmentResultTag(result: string) {
  const resultConfig: Record<string, { color: string; icon: React.ReactNode }> = {
    Pass: { color: 'green', icon: <CheckCircleOutlined /> },
    Fail: { color: 'red', icon: <CloseCircleOutlined /> },
    Conditional: { color: 'orange', icon: <QuestionCircleOutlined /> },
    'Conditional-Pass': { color: 'orange', icon: <QuestionCircleOutlined /> },
  };
  const config = resultConfig[result] || { color: 'default', icon: null };
  return (
    <Tag color={config.color} icon={config.icon}>
      {result}
    </Tag>
  );
}

export function getAssessmentScoreColor(percentage: number | null | undefined) {
  const pct = num(percentage, 0);
  if (pct >= 90) return '#52c41a';
  if (pct >= 80) return '#145A54';
  if (pct >= 70) return '#faad14';
  if (pct >= 60) return '#ff7a45';
  return '#f5222d';
}

export function getAssessmentWorkflowStep(assessment: { status?: string; approved_at?: string | null }) {
  switch (assessment.status) {
    case 'Draft':
      return 0;
    case 'Submitted':
    case 'Under-Review':
      return 1;
    case 'Evaluated':
    case 'Completed':
      return assessment.approved_at ? 3 : 2;
    case 'Approved':
      return 3;
    default:
      return 0;
  }
}
